import { inject, NgModule, Type } from '@angular/core';
import {
  provideRouter,
  Route,
  RouterModule,
  Routes,
  withComponentInputBinding,
  withInMemoryScrolling,
} from '@angular/router';
import { LocaleService, SUPPORTED_LOCALES } from './core/services/locale.service';
import { localeGuard } from './core/guards/locale.guard';
import { RouteKey, slugFor } from './core/i18n/route-slugs';
import {
  CouponPermission,
  InvoicePermission,
  RoomPermission,
  VendorPermission,
} from './core/models/entities/permission';
import { AuthenticatedGuard } from './core/guards/authenticated.guard';
import { RoomStepDirtyGuard } from './rooms/guards/room-step-dirty.guard';
import { HomePageComponent } from './public/pages/home-page/home-page.component';
import { MyBookingPage } from './public/pages/booking-detail-page/my-booking-page.component';
import { PermissionsPageComponent } from './public/pages/permissions-page/permissions-page.component';
import { GeneralTermsPageComponent } from './public/pages/general-terms-page/general-terms-page.component';
import { PrivacyPolicyPageComponent } from './public/pages/privacy-policy-page/privacy-policy-page.component';
import { PricingPageComponent } from './public/pages/pricing-page/pricing-page.component';
import { NotFoundPageComponent } from './core/pages/not-found-page/not-found-page.component';
import { UnlockDoorPageComponent } from './public/pages/unlock-door/unlock-door-page.component';
import { AboutPageComponent } from './public/pages/about-page.component';
import { PartnersResellersPageComponent } from './public/pages/partners-resellers-page/partners-resellers-page.component';
import { VendorSettingsPageComponent } from './settings/pages/vendor-settings-page/vendor-settings-page.component';
import { OnlinePaymentSetupPageComponent } from './settings/pages/online-payment-setup-page.component';
import { ContactPageComponent } from './public/pages/contact-page/contact-page.component';
import { VendorsListPageComponent } from './public/pages/vendors-list-page/vendors-list-page.component';
import { VendorPageComponent } from './public/pages/vendor-page/vendor-page.component';
import { VendorPrivacyPolicyPageComponent } from './public/pages/vendor-privacy-policy-page/vendor-privacy-policy-page.component';
import { RoomPageComponent } from './public/pages/room-page/room-page.component';
import { BookingPageComponent } from './public/pages/booking-page/booking-page.component';
import { BookingConfirmationPageComponent } from './public/pages/booking-confirmation-page/booking-confirmation-page.component';
import { VendorRegistrationPageComponent } from './auth/pages/vendor-registration-page/vendor-registration-page.component';

/**
 * Définition d'une route publique, indépendante de la langue. Le slug réel
 * (et donc le `path`) est résolu par langue via `ROUTE_SLUGS`. Le composant,
 * le titre et les `data` (b2c, hideFooter…) sont communs à toutes les langues.
 */
interface PublicRouteDef {
  key: RouteKey;
  component: Type<unknown>;
  title?: string;
  data?: Record<string, unknown>;
}

const PUBLIC_ROUTE_DEFS: PublicRouteDef[] = [
  {
    key: 'home',
    component: HomePageComponent,
    title: 'page-title.home',
  },
  {
    key: 'about',
    component: AboutPageComponent,
    title: 'page-title.about',
  },
  {
    key: 'pricing',
    component: PricingPageComponent,
    title: 'page-title.pricing',
  },
  {
    key: 'contact',
    component: ContactPageComponent,
    title: 'page-title.contact',
  },
  {
    key: 'partners',
    component: PartnersResellersPageComponent,
    title: 'page-title.partners',
  },
  {
    key: 'becomeHost',
    component: VendorRegistrationPageComponent,
    title: 'page-title.become-host',
  },
  {
    key: 'search',
    component: VendorsListPageComponent,
    title: 'page-title.search',
  },
  {
    key: 'vendor',
    component: VendorPageComponent,
    data: { b2c: true },
  },
  {
    key: 'vendorPrivacy',
    component: VendorPrivacyPolicyPageComponent,
    title: 'page-title.privacy',
    data: { b2c: true },
  },
  {
    key: 'room',
    component: RoomPageComponent,
    data: { b2c: true },
  },
  {
    key: 'booking',
    component: BookingPageComponent,
    data: { b2c: true, hideFooter: true, grayBackground: true },
  },
  {
    key: 'bookingConfirmation',
    component: BookingConfirmationPageComponent,
    title: 'page-title.booking-confirmation',
    data: { b2c: true },
  },
  {
    key: 'myBooking',
    component: MyBookingPage,
    title: 'page-title.my-booking',
    data: { b2c: true },
  },
  {
    key: 'permissions',
    component: PermissionsPageComponent,
    title: 'page-title.permissions',
  },
  {
    key: 'unlockDoor',
    component: UnlockDoorPageComponent,
    data: { b2c: true, hideNavbarAndFooter: true },
  },
];

/**
 * Construit les routes enfants d'un sous-arbre de langue donné, à partir des
 * définitions communes et des slugs traduits.
 */
function buildLocalizedChildren(locale: (typeof SUPPORTED_LOCALES)[number]): Routes {
  return PUBLIC_ROUTE_DEFS.map((def): Route => {
    const route: Route = {
      path: slugFor(def.key, locale),
      component: def.component,
      data: { ...(def.data ?? {}), routeKey: def.key },
    };
    if (def.title) {
      route.title = def.title;
    }
    // L'accueil (slug '') ne doit matcher que le préfixe nu (/fr, /en).
    if (def.key === 'home') {
      route.pathMatch = 'full';
    }
    return route;
  });
}

/**
 * Un sous-arbre par langue : `/fr/...`, `/en/...`. La garde `localeGuard`
 * active la bonne langue (via `data.locale`) avant le rendu des enfants.
 */
const localizedRoutes: Routes = SUPPORTED_LOCALES.map(
  (locale): Route => ({
    path: locale,
    canActivate: [localeGuard],
    data: { locale },
    children: buildLocalizedChildren(locale),
  }),
);

/**
 * Redirections 301 des anciennes URLs françaises non préfixées vers leur
 * équivalent `/fr/...`, pour préserver les liens entrants et le référencement.
 * Générées depuis les slugs français + quelques alias historiques.
 */
const legacyRedirects: Routes = [
  // Slugs français actuels → /fr/<slug>
  ...PUBLIC_ROUTE_DEFS.filter((def) => def.key !== 'home').map(
    (def): Route => {
      const frSlug = slugFor(def.key, 'fr');
      return {
        path: frSlug,
        redirectTo: `/fr/${frSlug}`,
        pathMatch: 'full',
      };
    },
  ),
  // Alias raccourcis historiques
  { path: 'a-propos', redirectTo: '/fr/louer-ma-salle/a-propos', pathMatch: 'full' },
  { path: 'tarifs', redirectTo: '/fr/louer-ma-salle/tarifs', pathMatch: 'full' },
  { path: 'contact', redirectTo: '/fr/louer-ma-salle/contact', pathMatch: 'full' },
  // « Devenir hôte » : déplacé depuis /auth vers une page publique localisée.
  { path: 'auth/devenir-un-hote', redirectTo: '/fr/devenir-un-hote', pathMatch: 'full' },
  { path: 'annuler-reservation/:token', redirectTo: '/fr/reservation/:token', pathMatch: 'full' },
  // Ancien préfixe « hote » → « louer-salle » (désormais sous /fr)
  { path: 'hote/:vendorSlug', redirectTo: '/fr/louer-salle/:vendorSlug', pathMatch: 'full' },
  {
    path: 'hote/:vendorSlug/politique-confidentialite',
    redirectTo: '/fr/louer-salle/:vendorSlug/politique-confidentialite',
    pathMatch: 'full',
  },
  {
    path: 'hote/:vendorSlug/salle/:id',
    redirectTo: '/fr/louer-salle/:vendorSlug/salle/:id',
    pathMatch: 'full',
  },
  {
    path: 'hote/:vendorSlug/salle/:id/reserver',
    redirectTo: '/fr/louer-salle/:vendorSlug/salle/:id/reserver',
    pathMatch: 'full',
  },
  {
    path: 'hote/:vendorSlug/salle/:id/confirmation',
    redirectTo: '/fr/louer-salle/:vendorSlug/salle/:id/confirmation',
    pathMatch: 'full',
  },
];

const routes: Routes = [
  // Accueil nu « / » : redirige vers la langue résolue (préférence → navigateur → fr).
  {
    path: '',
    pathMatch: 'full',
    redirectTo: () => {
      const localeService = inject(LocaleService);
      return `/${localeService.resolveInitialLocale()}`;
    },
  },
  ...localizedRoutes,
  // Pages légales : contenu uniquement en français, donc routes non préfixées
  // (hors arbre localisé /fr|/en).
  {
    path: 'conditions-generales',
    component: GeneralTermsPageComponent,
    title: 'page-title.terms',
  },
  {
    path: 'politique-confidentialite',
    component: PrivacyPolicyPageComponent,
    title: 'page-title.privacy',
  },
  ...legacyRedirects,
  {
    path: 'auth',
    loadChildren: () =>
      import('./auth/auth.module').then((mod) => mod.AuthModule),
  },
  {
    path: 'utilisateurs',
    loadChildren: () =>
      import('./users/users.module').then((mod) => mod.UsersModule),
    canActivate: [AuthenticatedGuard],
    canActivateChild: [AuthenticatedGuard],
    data: {
      manager: true,
    },
  },
  {
    path: 'salles',
    loadChildren: () =>
      import('./rooms/rooms.module').then((mod) => mod.RoomsModule),
    canActivate: [AuthenticatedGuard],
    canActivateChild: [AuthenticatedGuard],
    data: {
      manager: true,
      requiredPermission: RoomPermission.Store,
    },
  },
  {
    path: 'reservations',
    loadChildren: () =>
      import('./bookings/bookings.module').then((mod) => mod.BookingsModule),
    canActivate: [AuthenticatedGuard],
    canActivateChild: [AuthenticatedGuard],
    data: {
      manager: true,
    },
  },
  {
    path: 'facturation',
    loadChildren: () =>
      import('./invoices/invoices.module').then((mod) => mod.InvoicesModule),
    canActivate: [AuthenticatedGuard],
    canActivateChild: [AuthenticatedGuard],
    data: {
      manager: true,
      requiredPermission: InvoicePermission.Store,
    },
  },
  {
    path: 'reglages',
    loadChildren: () =>
      import('./settings/settings.module').then((mod) => mod.SettingsModule),
    canActivate: [AuthenticatedGuard],
    canActivateChild: [AuthenticatedGuard],
    data: {
      manager: true,
    },
  },
  {
    path: 'administration',
    redirectTo: 'administration/donnees',
  },
  {
    path: 'administration/donnees',
    component: VendorSettingsPageComponent,
    canActivate: [AuthenticatedGuard],
    canActivateChild: [AuthenticatedGuard],
    canDeactivate: [RoomStepDirtyGuard],
    title: 'page-title.org-data',
    data: {
      manager: true,
      requiredPermission: VendorPermission.Update,
    },
  },
  {
    path: 'administration/paiement-en-ligne',
    component: OnlinePaymentSetupPageComponent,
    canActivate: [AuthenticatedGuard],
    canActivateChild: [AuthenticatedGuard],
    canDeactivate: [RoomStepDirtyGuard],
    title: 'page-title.online-payment',
    data: {
      manager: true,
      requiredPermission: VendorPermission.Update,
    },
  },
  {
    path: 'compte',
    loadChildren: () =>
      import('./account/account.module').then((mod) => mod.AccountModule),
    canActivate: [AuthenticatedGuard],
    canActivateChild: [AuthenticatedGuard],
    data: {
      manager: true,
    },
  },
  {
    path: 'codes-promo',
    loadChildren: () =>
      import('./coupons/coupons.module').then((mod) => mod.CouponsModule),
    canActivate: [AuthenticatedGuard],
    canActivateChild: [AuthenticatedGuard],
    data: {
      manager: true,
      requiredPermission: CouponPermission.Store,
    },
  },
  {
    path: '404',
    component: NotFoundPageComponent,
    title: 'page-title.not-found',
  },
  {
    path: '**',
    redirectTo: '404',
  },
];

@NgModule({
  exports: [RouterModule],
  providers: [
    provideRouter(
      routes,
      withComponentInputBinding(),
      withInMemoryScrolling({
        scrollPositionRestoration: 'enabled',
        anchorScrolling: 'enabled',
      }),
    ),
  ],
})
export class AppRoutingModule {}
